const STORAGE_KEY = "offline_enabled";

/**
 * Whether the offline feature is switched on for THIS user.
 *
 * The authoritative value lives on the server, in the per-user
 * `users.offline_enabled` column toggled from Super Admin → Users, and arrives
 * with the login response. But three of the decisions it gates — registering the
 * service worker, persisting the store, deciding whether to keep a service worker
 * already installed — are made during boot, synchronously, before any request has
 * come back. So the value from the last login is mirrored into localStorage and
 * read from there here.
 *
 * Consequences, both deliberate:
 * - The very first load after a super admin enables a user behaves as "off"; the
 *   next load picks it up. Nothing is lost, the feature just starts one reload later.
 * - Default is OFF. Every user gets exactly today's behaviour until someone is
 *   explicitly opted in, which is what makes this safe to deploy to production.
 *
 * Cleared on logout (see authSaga's logoutUser), because it describes one user's
 * session and must not still be set for whoever reaches /login next.
 */
export const isOfflineFeatureEnabled = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // localStorage throws in some locked-down/private contexts — fail closed.
    return false;
  }
};

export const setOfflineFeatureEnabled = (enabled: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    // Nothing to do: the flag simply stays at its default for this session.
  }
};
